"use client";

import {
  useCallback,
  useEffect,
  useRef,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";

const DRAG_THRESHOLD = 8;
const GAP_FULL_BLEED = 20; // gap-5

/**
 * Horizontal drag strip matching big.dk/about: overflow-hidden parent +
 * translate3d on the flex track (not native overflow-x scroll).
 *
 * Capture starts only after the pointer moves past a threshold so card
 * checkbox clicks (title +) still work.
 *
 * When `snap` is set, pointer-up snaps to the nearest full-width slide
 * (careers process carousel: flex-[0_0_100%] + gap-5).
 */
export function DragScroll({
  children,
  className,
  trackClassName,
  snap = false,
  slideCount,
  onIndexChange,
  showEndSpacer = true,
}: {
  children: ReactNode;
  className?: string;
  trackClassName?: string;
  snap?: boolean;
  /** Required when snap is true — number of full-width slides. */
  slideCount?: number;
  onIndexChange?: (index: number) => void;
  showEndSpacer?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const xRef = useRef(0);
  const startXRef = useRef(0);
  const startOffsetRef = useRef(0);
  const pointerIdRef = useRef<number | null>(null);
  const draggingRef = useRef(false);
  const suppressClickRef = useRef(false);
  const onIndexChangeRef = useRef(onIndexChange);
  onIndexChangeRef.current = onIndexChange;

  const clamp = useCallback((value: number) => {
    const track = trackRef.current;
    if (!track) return 0;
    const parent = track.parentElement;
    if (!parent) return 0;
    const max = Math.max(0, track.scrollWidth - parent.clientWidth);
    return Math.min(0, Math.max(-max, value));
  }, []);

  const apply = useCallback(
    (value: number) => {
      const track = trackRef.current;
      if (!track) return;
      const next = clamp(value);
      xRef.current = next;
      track.style.transform = `translate3d(${next}px, 0px, 0px)`;
    },
    [clamp],
  );

  const snapToNearest = useCallback(() => {
    if (!snap || !slideCount || slideCount < 1) return;
    const track = trackRef.current;
    const parent = track?.parentElement;
    if (!track || !parent) return;
    const slideW = parent.clientWidth + GAP_FULL_BLEED;
    if (slideW <= 0) return;
    const raw = Math.round(-xRef.current / slideW);
    const index = Math.max(0, Math.min(slideCount - 1, raw));
    apply(-(index * slideW));
    onIndexChangeRef.current?.(index);
  }, [apply, snap, slideCount]);

  useEffect(() => {
    const onResize = () => {
      if (snap && slideCount) {
        snapToNearest();
      } else {
        apply(xRef.current);
      }
    };
    window.addEventListener("resize", onResize);
    apply(0);
    return () => window.removeEventListener("resize", onResize);
  }, [apply, snap, slideCount, snapToNearest]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    pointerIdRef.current = e.pointerId;
    draggingRef.current = false;
    startXRef.current = e.clientX;
    startOffsetRef.current = xRef.current;
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    const dx = e.clientX - startXRef.current;
    if (!draggingRef.current) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;
      draggingRef.current = true;
      suppressClickRef.current = true;
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }
    apply(startOffsetRef.current + dx);
  };

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    pointerIdRef.current = null;
    const wasDragging = draggingRef.current;
    draggingRef.current = false;
    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    } catch {
      // ignore
    }
    if (wasDragging && snap) snapToNearest();
  };

  const onClickCapture = (e: ReactMouseEvent) => {
    if (!suppressClickRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    suppressClickRef.current = false;
  };

  return (
    <div className={className ?? "w-full overflow-hidden"}>
      <div
        ref={trackRef}
        data-about-strip
        className={
          trackClassName ??
          "flex w-max cursor-grab flex-nowrap gap-[18px] pl-7 active:cursor-grabbing md:pl-[70px] lg:gap-[26px] lg:pl-[100px] xl:pl-[130px]"
        }
        style={{ transform: "translate3d(0px, 0px, 0px)", touchAction: "pan-y" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
      >
        {children}
        {showEndSpacer ? (
          <div
            className="w-7 min-w-0 flex-initial shrink-0 md:w-[70px] lg:w-[100px] xl:w-[130px]"
            aria-hidden
          />
        ) : null}
      </div>
    </div>
  );
}
